import * as React from 'react';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import {
  CartesianGrid,
  Label,
  ReferenceArea,
  ReferenceLine,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { IQuadrantReportState } from '../../../data/providers/reports/quadrantReportProvider';
import { Entity, ISelectableTag, ISize, ITag } from '../../../data/types';
import { roundValue } from '../../../utils/math';
import { testMetaString } from '../../../utils/testMetaString';
import { EPdfMode, shouldInvert } from '../../shared/pdf/Pdf';
import { LabelWithBackground } from './LabelWithBackground';
import { QuadrantTooltip } from './QuadrantTooltip';

interface IProps {
  reportState: IQuadrantReportState;
  pdfMode?: EPdfMode;
  quartileColors: string[];
}
interface IDataPoint {
  [key: string]: number | Entity | string;
  entity: Entity;
}

export const QuadrantGraph = (props: IProps): React.ReactElement => {
  const [data] = React.useMemo((): [IDataPoint[], number[], number[]] => {
    const mXVals: number[] = [];
    const mYVals: number[] = [];
    const mData: IDataPoint[] = [];

    for (const result of props.reportState.quadrantResults) {
      if (result.x.value !== null && result.y.value !== null) {
        mData.push({
          [`${result.x.testMeta} ${result.x.metricLabel}`]:
            result.x.zScore * 100,
          [`${result.y.testMeta} ${result.y.metricLabel}`]:
            result.y.zScore * 100,
          [`${result.x.testMeta} ${result.x.metricLabel}-val`]: `${result.x.value}${result.x.units}`,
          [`${result.y.testMeta} ${result.y.metricLabel}-val`]: `${result.y.value}${result.y.units}`,
          entity: result.entity,
        });

        mXVals.push(result.x.zScore);
        mYVals.push(result.y.zScore);
      }
    }

    return [mData, mXVals, mYVals];
  }, [JSON.stringify(props.reportState.quadrantResults)]);

  return (
    <React.Fragment>
      <div
        style={{
          height: '100mm',
          width: '100%',
          paddingBottom: '10px',
        }}
        className='graph-parent'
      >
        <AutoSizer>
          {(size: ISize): React.ReactElement => {
            return (
              <ScatterChart
                margin={{ top: 5, left: 5, right: 5, bottom: 10 }}
                height={size.height}
                width={size.width}
              >
                <ReferenceArea
                  x1={0}
                  x2={420}
                  y1={0}
                  y2={420}
                  yAxisId='left'
                  ifOverflow='extendDomain'
                  fill='transparent'
                  fillOpacity={0.2}
                  strokeOpacity={0}
                >
                  <Label
                    value='Quadrant I'
                    position='center'
                    fill='var(--color-text)'
                    strokeOpacity={0.1}
                  />
                </ReferenceArea>
                <ReferenceArea
                  x1={0}
                  x2={420}
                  y1={-420}
                  y2={0}
                  yAxisId='left'
                  ifOverflow='extendDomain'
                  fill='transparent'
                  fillOpacity={0.2}
                  strokeOpacity={0}
                >
                  <Label
                    value='Quadrant IV'
                    position='center'
                    fill='var(--color-text)'
                    strokeOpacity={0.1}
                  />
                </ReferenceArea>
                <ReferenceArea
                  x1={-420}
                  x2={0}
                  y1={0}
                  y2={420}
                  yAxisId='left'
                  ifOverflow='extendDomain'
                  fill='transparent'
                  fillOpacity={0.2}
                  strokeOpacity={0}
                >
                  <Label
                    value='Quadrant II'
                    position='center'
                    fill='var(--color-text)'
                    strokeOpacity={0.1}
                  />
                </ReferenceArea>
                <ReferenceArea
                  x1={-420}
                  x2={0}
                  y1={-420}
                  y2={0}
                  yAxisId='left'
                  ifOverflow='extendDomain'
                  fill='transparent'
                  fillOpacity={0.2}
                  strokeOpacity={0}
                >
                  <Label
                    value='Quadrant III'
                    position='center'
                    fill='var(--color-text)'
                    strokeOpacity={0.1}
                  />
                </ReferenceArea>
                <CartesianGrid stroke='#ccc' strokeOpacity={0.25} />
                <ReferenceLine
                  yAxisId='left'
                  y={0}
                  ifOverflow='extendDomain'
                  stroke='var(--color-primary)'
                  strokeWidth={3}
                  fill='var(--color-text)'
                  strokeOpacity={1}
                >
                  <Label
                    value={`${roundValue(
                      props.reportState.populationResult.y.mean,
                      props.reportState.populationResult.y.roundingPrecision
                    )}${props.reportState.populationResult.y.units}`}
                    content={
                      <LabelWithBackground
                        external={{
                          inverse: shouldInvert(props.pdfMode),
                          vertical: true,
                        }}
                      />
                    }
                    position='insideLeft'
                    fill='var(--color-text)'
                    dy={-10}
                  />
                </ReferenceLine>
                <ReferenceLine
                  yAxisId='left'
                  x={0}
                  ifOverflow='extendDomain'
                  stroke='var(--color-primary)'
                  strokeWidth={3}
                  strokeOpacity={1}
                >
                  <Label
                    position='bottom'
                    value={`${roundValue(
                      props.reportState.populationResult.x.mean,
                      props.reportState.populationResult.x.roundingPrecision
                    )}${props.reportState.populationResult.x.units}`}
                    fill='var(--color-text)'
                    content={
                      <LabelWithBackground
                        external={{ inverse: shouldInvert(props.pdfMode) }}
                      />
                    }
                  />
                </ReferenceLine>
                <XAxis
                  dataKey={`${testMetaString(
                    props.reportState.settings.settings.xAxis.testType.entity
                      .abbr,
                    props.reportState.settings.settings.xAxis.tags.map(
                      (t: ISelectableTag): ITag => t.entity
                    )
                  )} ${props.reportState.settings.settings.xAxis.metric.label}`}
                  tickCount={1}
                  ticks={[0]}
                  type='number'
                  axisLine={false}
                  tickLine={false}
                  label={''}
                  tick={{
                    fill: 'transparent',
                  }}
                  domain={[-420, 420]}
                >
                  <Label
                    fill='var(--color-text)'
                    value={
                      props.reportState.settings.settings.xAxis.metric.label
                    }
                    position='insideBottom'
                    offset={-5}
                  />
                  <Label
                    value={'Below Average'}
                    position='bottom'
                    dy={-30}
                    dx={-250}
                    fill='var(--color-text)'
                  />
                  <Label
                    value={'Above Average'}
                    position='bottom'
                    dy={-30}
                    dx={250}
                    fill='var(--color-text)'
                  />
                </XAxis>
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  yAxisId='left'
                  type='number'
                  tickCount={1}
                  label={''}
                  ticks={[0]}
                  orientation='left'
                  domain={[-420, 420]}
                  dataKey={`${testMetaString(
                    props.reportState.settings.settings.yAxis.testType.entity
                      .abbr,
                    props.reportState.settings.settings.yAxis.tags.map(
                      (t: ISelectableTag): ITag => t.entity
                    )
                  )} ${props.reportState.settings.settings.yAxis.metric.label}`}
                  tick={{
                    fill: 'transparent',
                  }}
                >
                  <Label
                    angle={270}
                    fill='var(--color-text)'
                    value={
                      props.reportState.settings.settings.yAxis.metric.label
                    }
                    position='left'
                    offset={-10}
                    dy={-100}
                  />
                  <Label
                    angle={270}
                    value={'Above Average'}
                    fill='var(--color-text)'
                    position='insideLeft'
                    offset={50}
                    dy={-35}
                  />
                  <Label
                    angle={270}
                    value={'Below Average'}
                    fill='var(--color-text)'
                    position='insideLeft'
                    offset={50}
                    dy={125}
                  />
                </YAxis>
                <Tooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  content={QuadrantTooltip}
                />
                <Scatter
                  name=''
                  data={data}
                  fill={
                    props.pdfMode === EPdfMode.PRINT
                      ? 'var(--color-scatter-primary-inverse)'
                      : 'var(--color-scatter-primary)'
                  }
                  stroke='var(--color-separator)'
                  line={false}
                  isAnimationActive={false}
                  lineType='fitting'
                  yAxisId='left'
                />
              </ScatterChart>
            );
          }}
        </AutoSizer>
      </div>
    </React.Fragment>
  );
};
